export type TaskStatus =
  | "backlog"
  | "todo"
  | "in-progress"
  | "in-review"
  | "done"
  | "cancelled"
  | "error";

const SIZE = 16;

export function StatusIcon({
  status,
  size = 16,
  className = "",
}: {
  status: TaskStatus;
  size?: number;
  className?: string;
}) {
  const scale = size / SIZE;
  const wrapStyle = scale !== 1 ? { transform: `scale(${scale})`, transformOrigin: "center" } : undefined;

  const icon = (() => {
    switch (status) {
      case "backlog":
        return (
          <svg width={SIZE} height={SIZE} viewBox="0 0 16 16" fill="none" className={`shrink-0 ${className}`}>
            <circle cx="8" cy="8" r="6.5" stroke="#474752" strokeWidth="1.5" strokeDasharray="3 2.5" fill="none" />
          </svg>
        );
      case "todo":
        return (
          <svg width={SIZE} height={SIZE} viewBox="0 0 16 16" fill="none" className={`shrink-0 ${className}`}>
            <circle cx="8" cy="8" r="6.5" stroke="#555560" strokeWidth="1.5" fill="none" />
          </svg>
        );
      case "in-progress":
        return (
          <svg width={SIZE} height={SIZE} viewBox="0 0 16 16" fill="none" className={`shrink-0 ${className}`}>
            <circle cx="8" cy="8" r="6.5" stroke="#f59e0b" strokeWidth="1.5" fill="none" />
            <path d="M8 1.5A6.5 6.5 0 0 0 1.5 8 6.5 6.5 0 0 0 8 14.5V1.5Z" fill="#f59e0b" />
          </svg>
        );
      case "in-review":
        return (
          <svg width={SIZE} height={SIZE} viewBox="0 0 16 16" fill="none" className={`shrink-0 ${className}`}>
            <circle cx="8" cy="8" r="6.5" stroke="#8b5cf6" strokeWidth="1.5" fill="none" />
            <ellipse cx="8" cy="8" rx="3" ry="2" stroke="#8b5cf6" strokeWidth="1.2" fill="none" />
            <circle cx="8" cy="8" r="0.8" fill="#8b5cf6" />
          </svg>
        );
      case "done":
        return (
          <svg width={SIZE} height={SIZE} viewBox="0 0 16 16" fill="none" className={`shrink-0 ${className}`}>
            <circle cx="8" cy="8" r="7" fill="#22c55e" />
            <path d="M5.5 8.2L7.2 9.9L10.5 6.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
        );
      case "cancelled":
        return (
          <svg width={SIZE} height={SIZE} viewBox="0 0 16 16" fill="none" className={`shrink-0 ${className}`}>
            <circle cx="8" cy="8" r="7" fill="#3E3E48" />
            <path d="M6 6L10 10M10 6L6 10" stroke="#8A8A95" strokeWidth="1.5" strokeLinecap="round" fill="none" />
          </svg>
        );
      case "error":
        return (
          <svg width={SIZE} height={SIZE} viewBox="0 0 16 16" fill="none" className={`shrink-0 ${className}`}>
            <circle cx="8" cy="8" r="7" fill="#ef4444" />
            <path d="M6 6L10 10M10 6L6 10" stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none" />
          </svg>
        );
      default:
        return (
          <svg width={SIZE} height={SIZE} viewBox="0 0 16 16" fill="none" className={`shrink-0 ${className}`}>
            <circle cx="8" cy="8" r="6.5" stroke="#474752" strokeWidth="1.5" fill="none" />
          </svg>
        );
    }
  })();

  return <span style={wrapStyle} className="inline-flex">{icon}</span>;
}

/** Map from run status to TaskStatus (for list view) */
export function mapRunStatus(status: string): TaskStatus {
  switch (status) {
    case "running": return "in-progress";
    case "blocked": return "error";
    case "ready": return "todo";
    case "review": return "in-review";
    case "done": return "done";
    case "archived": return "cancelled";
    default: return "backlog";
  }
}

/** Map from legacy run status names to TaskStatus */
export function mapProjectStatus(status: string): TaskStatus {
  switch (status) {
    case "healthy": return "done";
    case "degraded": return "in-progress";
    case "offline": return "cancelled";
    case "running": return "in-progress";
    case "blocked": return "error";
    case "ready": return "todo";
    case "review": return "in-review";
    case "done": return "done";
    case "archived": return "cancelled";
    default: return "backlog";
  }
}
